import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import MatchList from '@/components/MatchList';
import { Match, UserRole, MatchStatus } from '@/types';
import { useAuthStore } from '../store/authStore';
import { MatchingService } from '../services/matchingService';

const MatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, [user, tabValue, currentPage]);

  const fetchMatches = async () => {
    if (!user) return;
    setLoading(true);
    try {
      let data: Match[] = [];
      if (user.role === UserRole.CLIENT) {
        // Fetch matches specifically for this client
        data = await MatchingService.getMatchesForClient(user.id);
      } else if (user.role === UserRole.AGENT) {
        // Fetch matches for all clients managed by this agent
        data = await MatchingService.getMatchesForAgent(user.id);
      } else {
        // Admin: Fetch all recent matches
        const res = await MatchingService.getMatches(currentPage, 20);
        data = res.data || [];
      }

      // Filter by tab (status)
      if (tabValue === 1) {
        data = data.filter(m => m.status === MatchStatus.INTERESTED);
      } else if (tabValue === 2) {
        data = data.filter(m => m.status === MatchStatus.NOT_INTERESTED);
      }

      setMatches(data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMatchClick = (match: Match) => {
    navigate(`/matches/${match.id}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif' }}>
          {user?.role === UserRole.CLIENT ? 'Curated Recommendations' : 'Property Matches'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {user?.role === UserRole.CLIENT 
            ? 'AI-powered selections based on your lifestyle and preferences.'
            : 'AI-powered property recommendations for your Moroccan client pipeline.'}
        </Typography>
      </Box>

      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ px: 2 }}>
          <Tab label="New Suggestions" />
          <Tab label="Interested" />
          <Tab label="Archived" />
        </Tabs>
      </Paper>

      <MatchList
        matches={matches}
        loading={loading}
        onMatchClick={handleMatchClick}
        pagination={{
          page: currentPage,
          totalPages: 1, // Simplified for now
          onPageChange: handlePageChange,
        }}
      />
    </Box>
  );
};

export default MatchesPage;
